import { contentLooksLikePdfSource } from "./organizedTourContentDetect";
import { sanitizeOrganizedTourQuoteText } from "../modules/organizedTours/utils/sanitizeOrganizedTourQuoteText";

/** Heuristic: block LDS pages mistakenly shown as plain text (CSS / full HTML / PDF bytes). */
export function orgTourInfoContentLooksLikeRawSource(s) {
  if (s == null || typeof s !== "string") return false;
  const t = s.trim();
  if (contentLooksLikePdfSource(t)) return true;
  if (t.length < 48) return false;
  if (/^\s*@(import|charset|font-face|media)\b/im.test(t)) return true;
  if (/\bDIV\.[a-zA-Z_][\w-]*\s*\{/m.test(t) && /\{[^}]{0,600}\}/.test(t)) return true;
  if (/^\s*<!DOCTYPE\s+html/i.test(t) && /<style[^>]*>/i.test(t)) return true;
  if (/<\s*html[^>]*>/i.test(t) && /<\s*head[^>]*>/i.test(t) && t.length > 1500) return true;
  return false;
}

const MIN_HTML_VISIBLE_CHARS = 18;

function flattenedVisibleApprox(html, maxScan = 24000) {
  if (!html || typeof html !== "string") return "";
  const trimmed = html.length > maxScan ? html.slice(0, maxScan) : html;
  return trimmed
    .replace(/<(?:script|style)[^>]*>[\s\S]*?<\/(?:script|style)>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** נוסח עדין כשאין טקסט/מסלול מלא מהספק — לא להציג כאירוע שגיאה */
export const ORG_TOUR_ITINERARY_SOFT_NOTICE_HE =
  "פירוט מלא של המסלול אינו זמין כרגע.";

/** @deprecated השתמשו ב־ORG_TOUR_ITINERARY_SOFT_NOTICE_HE + resolveOrganizedTourItineraryFallback */
export const ORG_TOUR_INFO_DISPLAY_FALLBACK_HE = ORG_TOUR_ITINERARY_SOFT_NOTICE_HE;

/**
 * @param {{ contentHtmlSanitized?: string|null, contentText?: string|null }} payload
 * @returns {{ mode: 'html'|'text'|'none', html: string|null, text: string|null, blockedRaw: boolean, fallbackReason: string|null }}
 */
export function resolveOrgTourInfoDisplay(payload) {
  const rawHtml = payload?.contentHtmlSanitized;
  const rawText = payload?.contentText;
  const html = typeof rawHtml === "string" ? rawHtml.trim() : "";
  const text = typeof rawText === "string" ? rawText.trim() : "";

  let blockedRaw = false;
  let htmlLooksRaw = false;
  let textLooksRaw = false;
  if (html && orgTourInfoContentLooksLikeRawSource(html)) {
    blockedRaw = true;
    htmlLooksRaw = true;
  }
  if (text && orgTourInfoContentLooksLikeRawSource(text)) {
    blockedRaw = true;
    textLooksRaw = true;
  }

  const safeHtmlRaw = html && !htmlLooksRaw ? html : "";
  const safeTextRaw = text && !textLooksRaw ? text : "";
  // Strip supplier tokens (e.g. ###SUPPLIER_PREPARED###) from profile itinerary body.
  const safeHtml = safeHtmlRaw
    ? safeHtmlRaw.replace(/###\s*SUPPLIER_PREPARED\s*###/gi, "")
    : "";
  const safeText = safeTextRaw ? sanitizeOrganizedTourQuoteText(safeTextRaw) : "";

  if (blockedRaw && safeHtml === "" && safeText === "") {
    const fallbackReason =
      htmlLooksRaw && textLooksRaw
        ? "blocked_likely_raw_both_channels"
        : htmlLooksRaw
          ? "blocked_likely_raw_html_channel"
          : "blocked_likely_raw_text_channel";
    return { mode: "none", html: null, text: null, blockedRaw, fallbackReason };
  }

  const htmlVisibleLen = safeHtml ? flattenedVisibleApprox(safeHtml).length : 0;
  const htmlStrongEnough = htmlVisibleLen >= MIN_HTML_VISIBLE_CHARS;

  if (safeHtml && htmlStrongEnough) {
    return {
      mode: "html",
      html: safeHtml,
      text: safeText || null,
      blockedRaw,
      fallbackReason: null,
    };
  }

  if (safeText && (safeHtml === "" || !htmlStrongEnough)) {
    return {
      mode: "text",
      html: null,
      text: safeText,
      blockedRaw,
      fallbackReason: null,
    };
  }

  if (safeHtml) {
    return {
      mode: "html",
      html: safeHtml,
      text: safeText || null,
      blockedRaw,
      fallbackReason:
        htmlVisibleLen > 0
          ? "html_below_visible_threshold_last_resort"
          : "html_markup_only_visible_empty_last_resort",
    };
  }

  if (!html && !text) {
    return {
      mode: "none",
      html: null,
      text: null,
      blockedRaw,
      fallbackReason: "missing_both_payload_fields",
    };
  }

  return {
    mode: "none",
    html: null,
    text: null,
    blockedRaw,
    fallbackReason:
      blockedRaw && !safeHtml && !safeText
        ? "blocked_raw_removed_all_safe_content"
        : "no_usable_plain_or_html_chunks",
  };
}

/**
 * Last-mile guard for dangerouslySetInnerHTML: never inject if content matches raw-source heuristics.
 * @param {{ mode: string, html?: string|null }} display — output of resolveOrgTourInfoDisplay
 * @returns {string|null}
 */
export function getOrgTourInfoDangerousInnerHtml(display) {
  if (!display || display.mode !== "html") return null;
  const html = display.html;
  if (typeof html !== "string" || !html.trim()) return null;
  if (orgTourInfoContentLooksLikeRawSource(html)) return null;
  return html;
}

/**
 * Same as HTML guard but for plain-text branch (CSS-as-text edge case).
 * @param {{ mode: string, text?: string|null }} display
 * @returns {string|null}
 */
export function getOrgTourInfoPlainTextForDisplay(display) {
  if (!display || display.mode !== "text") return null;
  const text = display.text;
  if (typeof text !== "string" || !text.trim()) return null;
  if (orgTourInfoContentLooksLikeRawSource(text)) return null;
  return text;
}
